import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Download, Settings2, Receipt, Mail, Repeat, PlayCircle, CheckCircle2, Undo2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA"); };
const monthEnd = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toLocaleDateString("en-CA"); };
const money = (x) => `$${Number(x || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EMPTY_BILLER = { name: "ArmorBatch Global", abn: "", address: "", email: "", phone: "", bsb: "", account_number: "", account_name: "" };
const EMPTY_SUB = { enabled: false, amount: 0, plan: "", day_of_month: 1, auto_email: true };

function newInvoice(org) {
  const d = new Date();
  const seq = String(Math.floor(d.getTime() / 1000)).slice(-4);
  return {
    invoice_number: `ABG-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}-${seq}`,
    issue_date: today(),
    due_date: addDays(14),
    period_from: monthStart(),
    period_to: monthEnd(),
    bill_to: { name: org?.name || "", abn: "", address: "", email: "" },
    line_items: [{ description: `ArmorBatch Global — ${org?.plan || "subscription"} plan (monthly)`, qty: 1, unit_price: 0 }],
    notes: "Thank you for your business. Payment due within 14 days.",
  };
}

export default function SaasInvoiceDialog({ org, open, onOpenChange }) {
  const [list, setList] = useState([]);
  const [biller, setBiller] = useState(EMPTY_BILLER);
  const [showBiller, setShowBiller] = useState(false);
  const [form, setForm] = useState(newInvoice(org));
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailingId, setEmailingId] = useState(null);
  const [subcfg, setSubcfg] = useState(EMPTY_SUB);
  const [showSub, setShowSub] = useState(false);
  const [savingSub, setSavingSub] = useState(false);
  const [runningBilling, setRunningBilling] = useState(false);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    if (!org) return;
    try {
      const [inv, bl, sb] = await Promise.all([
        api.get(`/superadmin/orgs/${org.id}/invoices`),
        api.get(`/superadmin/biller`),
        api.get(`/superadmin/orgs/${org.id}/subscription`),
      ]);
      setList(inv.data || []);
      setBiller({ ...EMPTY_BILLER, ...(bl.data || {}) });
      setSubcfg({ ...EMPTY_SUB, ...(sb.data || {}) });
    } catch (e) { toast.error("Couldn't load invoices"); }
  }, [org]);

  useEffect(() => { if (open) { load(); setForm(newInvoice(org)); setEditingId(null); } }, [open, org, load]);

  const sub = form.line_items.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_price) || 0), 0);
  const gst = sub * 0.1;
  const total = sub + gst;

  const setLi = (i, k, v) => setForm((f) => ({ ...f, line_items: f.line_items.map((li, idx) => idx === i ? { ...li, [k]: v } : li) }));
  const addLi = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", qty: 1, unit_price: 0 }] }));
  const rmLi = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
  const setBt = (k, v) => setForm((f) => ({ ...f, bill_to: { ...f.bill_to, [k]: v } }));

  const saveBiller = async () => {
    try { await api.put(`/superadmin/biller`, biller); toast.success("Business & bank details saved"); }
    catch { toast.error("Couldn't save details"); }
  };

  const save = async () => {
    if (!form.invoice_number.trim()) return toast.error("Invoice number required");
    setBusy(true);
    try {
      if (editingId) await api.put(`/superadmin/invoices/${editingId}`, form);
      else await api.post(`/superadmin/orgs/${org.id}/invoices`, form);
      toast.success(editingId ? "Invoice updated" : "Invoice created");
      setForm(newInvoice(org)); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save invoice"); }
    finally { setBusy(false); }
  };

  const edit = (inv) => {
    setEditingId(inv.id);
    setForm({ invoice_number: inv.invoice_number, issue_date: inv.issue_date, due_date: inv.due_date, period_from: inv.period_from || "", period_to: inv.period_to || "", bill_to: inv.bill_to || {}, line_items: inv.line_items || [], notes: inv.notes || "" });
  };
  const del = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    try { await api.delete(`/superadmin/invoices/${inv.id}`); load(); if (editingId === inv.id) { setForm(newInvoice(org)); setEditingId(null); } }
    catch { toast.error("Couldn't delete"); }
  };
  const openPdf = (inv) => window.open(`${BACKEND}/api/superadmin/invoices/${inv.id}/pdf`, "_blank");

  const emailInvoice = async (inv) => {
    const fallback = inv.bill_to?.email || "";
    const to = window.prompt(`Email invoice ${inv.invoice_number} to:`, fallback);
    if (to === null) return;                 // cancelled
    setEmailingId(inv.id);
    try {
      const { data } = await api.post(`/superadmin/invoices/${inv.id}/email`, to.trim() ? { to: to.trim() } : {});
      toast.success(`Invoice emailed to ${data.to}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't email invoice");
    } finally {
      setEmailingId(null);
    }
  };

  const saveSubscription = async () => {
    setSavingSub(true);
    try {
      await api.put(`/superadmin/orgs/${org.id}/subscription`, { ...subcfg, amount: Number(subcfg.amount) || 0, day_of_month: Number(subcfg.day_of_month) || 1 });
      toast.success("Subscription settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save subscription"); }
    finally { setSavingSub(false); }
  };

  const runBilling = async () => {
    setRunningBilling(true);
    try {
      const { data } = await api.post(`/superadmin/billing/run`);
      toast.success(`Billing run: ${data.generated} generated · ${data.emailed} emailed · ${data.reminders} reminder${data.reminders === 1 ? "" : "s"} sent`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Billing run failed"); }
    finally { setRunningBilling(false); }
  };

  const markPaid = async (inv) => {
    const ref = window.prompt(`Mark ${inv.invoice_number} paid — payment reference / notes (optional):`, inv.paid_reference || "");
    if (ref === null) return;                // cancelled
    setActingId(inv.id);
    try {
      const { data } = await api.post(`/superadmin/invoices/${inv.id}/mark-paid`, { reference: ref });
      toast.success(data.receipt_sent ? "Marked paid · receipt emailed" : "Marked paid");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't mark paid"); }
    finally { setActingId(null); }
  };

  const markUnpaid = async (inv) => {
    setActingId(inv.id);
    try {
      await api.post(`/superadmin/invoices/${inv.id}/mark-unpaid`);
      toast.success("Marked unpaid");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
    finally { setActingId(null); }
  };

  const inputCls = "rounded-none bg-transparent border-industrial text-xs";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-surface border-industrial max-h-[92vh] overflow-y-auto" data-testid="saas-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 heading"><Receipt className="w-4 h-4 text-primary-orange" /> Subscription Invoices · {org?.name}</DialogTitle>
          <DialogDescription className="text-xs text-muted-industrial">Create and edit ArmorBatch Global tax invoices for this organization, then download the PDF.</DialogDescription>
        </DialogHeader>

        {/* Biller profile */}
        <div className="border border-industrial">
          <button type="button" data-testid="biller-toggle" onClick={() => setShowBiller((s) => !s)}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs mono uppercase tracking-widest text-muted-industrial hover:text-primary-orange">
            <Settings2 className="w-3.5 h-3.5" /> Your business & bank details (shown on every invoice) {showBiller ? "▾" : "▸"}
          </button>
          {showBiller && (
            <div className="p-3 grid grid-cols-2 gap-2 border-t border-industrial">
              {[["name", "Business name"], ["abn", "ABN"], ["email", "Email"], ["phone", "Phone"]].map(([k, l]) => (
                <div key={k}><Label className="label-tag">{l}</Label><Input data-testid={`biller-${k}`} value={biller[k] || ""} onChange={(e) => setBiller({ ...biller, [k]: e.target.value })} className={inputCls} /></div>
              ))}
              <div className="col-span-2"><Label className="label-tag">Address</Label><Input data-testid="biller-address" value={biller.address || ""} onChange={(e) => setBiller({ ...biller, address: e.target.value })} className={inputCls} /></div>
              {[["account_name", "Account name"], ["bsb", "BSB"], ["account_number", "Account number"]].map(([k, l]) => (
                <div key={k}><Label className="label-tag">{l}</Label><Input data-testid={`biller-${k}`} value={biller[k] || ""} onChange={(e) => setBiller({ ...biller, [k]: e.target.value })} className={inputCls} /></div>
              ))}
              <div className="col-span-2 flex justify-end"><Button data-testid="biller-save" size="sm" className="rounded-none bg-primary-orange text-white" onClick={saveBiller}>Save details</Button></div>
            </div>
          )}
        </div>

        {/* Recurring subscription */}
        <div className="border border-industrial" data-testid="subscription-panel">
          <button type="button" data-testid="sub-toggle" onClick={() => setShowSub((s) => !s)}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs mono uppercase tracking-widest text-muted-industrial hover:text-primary-orange">
            <Repeat className="w-3.5 h-3.5" /> Recurring monthly billing {subcfg.enabled ? <span className="text-green-500 normal-case">· ON</span> : <span className="normal-case">· off</span>} {showSub ? "▾" : "▸"}
          </button>
          {showSub && (
            <div className="p-3 border-t border-industrial space-y-3">
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="sub-enabled-label">
                <input type="checkbox" data-testid="sub-enabled" checked={!!subcfg.enabled} onChange={(e) => setSubcfg({ ...subcfg, enabled: e.target.checked })} />
                Auto-generate this org's subscription invoice every month
              </label>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="label-tag">Monthly amount (ex GST)</Label><Input data-testid="sub-amount" type="number" value={subcfg.amount} onChange={(e) => setSubcfg({ ...subcfg, amount: e.target.value })} className={inputCls} /></div>
                <div><Label className="label-tag">Billing day of month</Label><Input data-testid="sub-day" type="number" min={1} max={28} value={subcfg.day_of_month} onChange={(e) => setSubcfg({ ...subcfg, day_of_month: e.target.value })} className={inputCls} /></div>
                <div className="flex items-end"><label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" data-testid="sub-autoemail" checked={!!subcfg.auto_email} onChange={(e) => setSubcfg({ ...subcfg, auto_email: e.target.checked })} /> Auto-email on generate</label></div>
              </div>
              <div><Label className="label-tag">Line description</Label><Input data-testid="sub-plan" value={subcfg.plan || ""} onChange={(e) => setSubcfg({ ...subcfg, plan: e.target.value })} placeholder="ArmorBatch Global — subscription (monthly)" className={inputCls} /></div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-industrial">Runs nightly. Generated once per calendar month on/after the billing day. Overdue unpaid invoices are nudged every 7 days.</span>
                <Button data-testid="sub-save" size="sm" className="rounded-none bg-primary-orange text-white" disabled={savingSub} onClick={saveSubscription}>{savingSub ? "Saving…" : "Save subscription"}</Button>
              </div>
            </div>
          )}
        </div>
        <div className="border border-industrial p-3 space-y-3">
          <div className="text-xs mono uppercase tracking-widest text-primary-orange">{editingId ? "Edit invoice" : "New invoice"}</div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="label-tag">Invoice #</Label><Input data-testid="inv-number" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className={inputCls} /></div>
            <div><Label className="label-tag">Issue date</Label><Input data-testid="inv-issue" type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className={inputCls} /></div>
            <div><Label className="label-tag">Due date</Label><Input data-testid="inv-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inputCls} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="label-tag">Service period — from</Label><Input data-testid="inv-period-from" type="date" value={form.period_from || ""} onChange={(e) => setForm({ ...form, period_from: e.target.value })} className={inputCls} /></div>
            <div><Label className="label-tag">Service period — to</Label><Input data-testid="inv-period-to" type="date" value={form.period_to || ""} onChange={(e) => setForm({ ...form, period_to: e.target.value })} className={inputCls} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[["name", "Bill to — name"], ["email", "Email"], ["abn", "ABN"], ["address", "Address"]].map(([k, l]) => (
              <div key={k} className={k === "address" ? "col-span-2" : ""}><Label className="label-tag">{l}</Label><Input data-testid={`bt-${k}`} value={form.bill_to[k] || ""} onChange={(e) => setBt(k, e.target.value)} className={inputCls} /></div>
            ))}
          </div>

          {/* line items */}
          <div className="space-y-1.5">
            <div className="grid grid-cols-[1fr_70px_100px_90px_28px] gap-2 text-[10px] mono uppercase text-muted-industrial px-0.5">
              <span>Description</span><span className="text-right">Qty</span><span className="text-right">Unit $</span><span className="text-right">Amount</span><span />
            </div>
            {form.line_items.map((li, i) => (
              <div key={i} className="grid grid-cols-[1fr_70px_100px_90px_28px] gap-2 items-center" data-testid={`inv-line-${i}`}>
                <Input data-testid={`line-desc-${i}`} value={li.description} onChange={(e) => setLi(i, "description", e.target.value)} className={inputCls} />
                <Input data-testid={`line-qty-${i}`} type="number" value={li.qty} onChange={(e) => setLi(i, "qty", e.target.value)} className={`${inputCls} text-right`} />
                <Input data-testid={`line-price-${i}`} type="number" value={li.unit_price} onChange={(e) => setLi(i, "unit_price", e.target.value)} className={`${inputCls} text-right`} />
                <span className="text-right text-xs mono tabular-nums">{money((Number(li.qty) || 0) * (Number(li.unit_price) || 0))}</span>
                <button type="button" data-testid={`line-remove-${i}`} onClick={() => rmLi(i)} className="text-muted-industrial hover:text-red-400" disabled={form.line_items.length === 1}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <Button data-testid="line-add" size="sm" variant="outline" className="rounded-none text-xs" onClick={addLi}><Plus className="w-3.5 h-3.5 mr-1" /> Add line</Button>
          </div>

          <div><Label className="label-tag">Notes</Label><Input data-testid="inv-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} /></div>

          <div className="flex items-center justify-between border-t border-industrial pt-2">
            <div className="text-xs mono tabular-nums text-muted-industrial" data-testid="inv-totals">
              Subtotal {money(sub)} · GST {money(gst)} · <span className="text-white font-bold">Total {money(total)}</span>
            </div>
            <div className="flex gap-2">
              {editingId && <Button size="sm" variant="outline" className="rounded-none text-xs" onClick={() => { setForm(newInvoice(org)); setEditingId(null); }}>Cancel edit</Button>}
              <Button data-testid="inv-save" size="sm" className="rounded-none bg-primary-orange text-white" disabled={busy} onClick={save}>{busy ? "Saving…" : editingId ? "Update invoice" : "Create invoice"}</Button>
            </div>
          </div>
        </div>

        {/* existing invoices */}
        <div className="border border-industrial">
          <div className="px-3 py-2 flex items-center justify-between border-b border-industrial">
            <span className="text-xs mono uppercase tracking-widest text-muted-industrial">Issued invoices ({list.length})</span>
            <Button data-testid="run-billing-now" size="sm" variant="outline" className="rounded-none text-xs h-7" disabled={runningBilling} onClick={runBilling}>
              <PlayCircle className="w-3.5 h-3.5 mr-1" />{runningBilling ? "Running…" : "Run billing now"}
            </Button>
          </div>
          {list.length === 0 ? (
            <div className="px-3 py-4 text-xs text-muted-industrial">No invoices yet.</div>
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {list.map((inv) => {
                  const paid = inv.status === "paid";
                  return (
                  <tr key={inv.id} className="border-t border-industrial/60" data-testid={`inv-row-${inv.invoice_number}`}>
                    <td className="px-3 py-2 mono font-bold">
                      {inv.invoice_number}
                      {inv.auto_generated && <span className="ml-1.5 text-[9px] text-muted-industrial" title="Auto-generated subscription invoice">AUTO</span>}
                    </td>
                    <td className="px-2 text-muted-industrial">{inv.issue_date}</td>
                    <td className="px-2 text-right tabular-nums">{money(inv.total)}</td>
                    <td className="px-2" data-testid={`inv-status-${inv.invoice_number}`}>
                      {paid ? (
                        <span className="inline-flex items-center gap-1 text-green-500 text-[10px] uppercase tracking-wider"><CheckCircle2 className="w-3 h-3" /> Paid{inv.paid_date ? ` · ${inv.paid_date}` : ""}</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider text-muted-industrial">{inv.status || "issued"}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {paid ? (
                        <button type="button" data-testid={`inv-unpaid-${inv.invoice_number}`} onClick={() => markUnpaid(inv)} disabled={actingId === inv.id} title="Mark unpaid" className="text-muted-industrial hover:text-amber-400 mr-3 disabled:opacity-50"><Undo2 className="w-4 h-4 inline" /></button>
                      ) : (
                        <button type="button" data-testid={`inv-paid-${inv.invoice_number}`} onClick={() => markPaid(inv)} disabled={actingId === inv.id} title="Mark paid (sends receipt)" className="text-muted-industrial hover:text-green-500 mr-3 disabled:opacity-50"><CheckCircle2 className="w-4 h-4 inline" /></button>
                      )}
                      <button type="button" data-testid={`inv-pdf-${inv.invoice_number}`} onClick={() => openPdf(inv)} title="Download PDF" className="text-muted-industrial hover:text-primary-orange mr-3"><Download className="w-4 h-4 inline" /></button>
                      <button type="button" data-testid={`inv-email-${inv.invoice_number}`} onClick={() => emailInvoice(inv)} disabled={emailingId === inv.id} title="Email PDF to organization" className="text-muted-industrial hover:text-primary-orange mr-3 disabled:opacity-50"><Mail className="w-4 h-4 inline" /></button>
                      <button type="button" data-testid={`inv-edit-${inv.invoice_number}`} onClick={() => edit(inv)} className="text-muted-industrial hover:text-primary-orange mr-3 underline">Edit</button>
                      <button type="button" data-testid={`inv-del-${inv.invoice_number}`} onClick={() => del(inv)} className="text-muted-industrial hover:text-red-400"><Trash2 className="w-4 h-4 inline" /></button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
